/**
 * 배관(연결관) 최대유입 — 실제 배관 수력학(마찰손실) 역산.
 *
 * ⚠ **이것도 표준식이 아니다.** KOSHA·API 는 이 케이스에 "최대 유입량"
 *   이라고만 하고 구하는 방법을 정하지 않는다. 여기서는 "사내 설계기준
 *   유속"으로 근사하는 대신, 이 앱의 배관 검토 탭과 **같은** Darcy–
 *   Weisbach + Colebrook–White 식으로 실제 배관(내경·길이·부속·조도)이
 *   주어진 압력차에서 얼마나 흘려보낼 수 있는지 **역산**한다.
 *   (정방향: 유량 → 압력손실. 여기서는 압력차 → 유량으로 뒤집는다.)
 *
 * 압력차가 정해져 있고 유량이 미지수이므로, 압력손실이 유량의 단조증가
 * 함수라는 점을 이용해 이분법으로 푼다.
 *
 * 단위: 압력 kPa(절대) · 밀도 kg/m³ · 점도 Pa·s · 내경 mm · 길이 m ·
 *       유량 kg/h
 */
import {
  buildSegment,
  pressureDrop,
  type LossMethod,
} from "./pipeFlow";

function invalid(message: string): { ok: false; reason: "invalid-input"; message: string } {
  return { ok: false, reason: "invalid-input", message };
}

function checkPositive(label: string, v: number): string | null {
  return !Number.isFinite(v) || v <= 0
    ? `${label} 은(는) 0보다 큰 유한한 값이어야 합니다.`
    : null;
}

export interface PipeInflowInput {
  readonly innerDiameterMm: number;
  /** 직관 길이 (m) */
  readonly straightLengthM: number;
  /** 부속 등가길이 합 (m). 등가길이 방식(L/D)으로 이미 환산한 값. */
  readonly fittingEquivalentLengthM: number;
  /** 절대조도 (mm) */
  readonly roughnessMm: number;
  /** 구동측(상류, 감압 전) 절대압 (kPa) */
  readonly upstreamPressureKpaAbs: number;
  /** 받는측(하류, 감압 후 — 보통 보호 대상 설비의 운전압) 절대압 (kPa) */
  readonly downstreamPressureKpaAbs: number;
  readonly densityKgM3: number;
  /** 점도 (Pa·s) */
  readonly viscosityPaS: number;
}

export interface PipeInflowSuccess {
  readonly ok: true;
  readonly massFlowKgH: number;
  readonly velocityMs: number;
  readonly reynolds: number;
  readonly frictionFactor: number;
  readonly warnings: readonly string[];
}
export interface PipeInflowFailure {
  readonly ok: false;
  readonly reason: "invalid-input" | "no-solution";
  readonly message: string;
}
export type PipeInflowResult = PipeInflowSuccess | PipeInflowFailure;

/** 압력차에서 배관이 흘려보낼 수 있는 유량을 이분법으로 역산한다. */
export function pipeInflowFromPressureDifference(
  input: PipeInflowInput,
): PipeInflowResult {
  for (const [label, v] of [
    ["관 내경", input.innerDiameterMm],
    ["밀도", input.densityKgM3],
    ["점도", input.viscosityPaS],
  ] as const) {
    const err = checkPositive(label, v);
    if (err) return invalid(err);
  }
  if (input.straightLengthM < 0 || input.fittingEquivalentLengthM < 0) {
    return invalid("길이는 0 이상이어야 합니다.");
  }
  if (input.roughnessMm < 0) return invalid("조도는 0 이상이어야 합니다.");

  const { upstreamPressureKpaAbs: p1, downstreamPressureKpaAbs: p2 } = input;
  if (!Number.isFinite(p1) || !Number.isFinite(p2)) {
    return invalid("압력이 올바르지 않습니다.");
  }
  if (p1 <= p2) {
    return invalid(
      "상류(감압 전) 압력이 하류(감압 후) 압력보다 커야 합니다. 두 값이 " +
        "절대압인지 확인하십시오.",
    );
  }
  const targetPa = (p1 - p2) * 1000;

  const dM = input.innerDiameterMm / 1000;
  const method: LossMethod = "equivalent-length";
  const segment = buildSegment(
    input.straightLengthM,
    dM,
    input.roughnessMm / 1000,
    {
      equivalentLengthM: input.fittingEquivalentLengthM,
      headOnlyK: 0,
      totalKEquivalent: 0,
    },
    method,
  );

  const dropAt = (massFlowKgS: number) =>
    pressureDrop(segment, massFlowKgS, input.densityKgM3, input.viscosityPaS)
      .pressureDropPa;

  // 이분법 상·하한을 잡는다 — 위쪽 한계를 필요하면 계속 키운다.
  let lo = 0;
  let hi = 1; // kg/s
  let iterations = 0;
  while (dropAt(hi) < targetPa) {
    hi *= 2;
    iterations += 1;
    if (iterations > 60) {
      return {
        ok: false,
        reason: "no-solution",
        message: "이분법 상한을 넘어섰습니다 — 입력값을 확인하십시오.",
      };
    }
  }

  for (let i = 0; i < 80; i++) {
    const mid = (lo + hi) / 2;
    if (dropAt(mid) < targetPa) lo = mid;
    else hi = mid;
  }
  const massFlowKgS = (lo + hi) / 2;
  const result = pressureDrop(
    segment,
    massFlowKgS,
    input.densityKgM3,
    input.viscosityPaS,
  );

  return {
    ok: true,
    massFlowKgH: massFlowKgS * 3600,
    velocityMs: result.velocityMs,
    reynolds: result.reynolds,
    frictionFactor: result.frictionFactor,
    warnings: [
      "이 배관 자체를 최대유입 경로로 보고, 이 앱의 배관 검토 탭과 같은 " +
        "Darcy–Weisbach + Colebrook–White 식으로 압력차에서 유량을 " +
        "역산한 값입니다. KOSHA·API 표준이 지정한 식이 아닙니다.",
      "회수 가능한 속도수두·위치수두는 반영하지 않았습니다(비회복 손실만 " +
        "고려) — 실제 배치에 따라 유량이 이보다 작을 수 있습니다.",
    ],
  };
}
